import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, History } from "lucide-react";

const STATUSES = [
  { v: "all", label: "All" },
  { v: "new", label: "New" },
  { v: "replied", label: "Replied" },
  { v: "ignored", label: "Ignored" },
  { v: "archived", label: "Archived" },
  { v: "deleted", label: "Deleted" },
];

const STATUS_COLORS = {
  new: "#B25D38",
  replied: "#5C6B52",
  ignored: "#7A7A7A",
  archived: "#5A6B7A",
  deleted: "#B23A3A",
};

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/messages", { params: { status } });
      setItems(data.items);
      setCounts(data.counts);
    } catch (e) {
      toast.error("Failed to load messages");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const open = async (m) => {
    setSelected(m);
    try {
      const { data } = await http.get("/admin/events", { params: { entity_type: "message", entity_id: m.id, limit: 100 } });
      setHistory(data.items);
    } catch { setHistory([]); }
  };

  const setStatusOn = async (m, newStatus, comment) => {
    try {
      const { data } = await http.patch(`/admin/messages/${m.id}`, { status: newStatus, comment: comment || "" });
      toast.success(`Marked as ${newStatus}`);
      setItems((xs) => xs.map((x) => (x.id === m.id ? data : x)));
      setSelected(data);
      open(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/events?entity_type=message&format=csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="admin-messages-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">MESSAGES</div>
          <h2 className="font-display text-3xl">Contact inbox</h2>
        </div>
        <button onClick={exportCSV} className="wofy-btn-ghost text-sm">Export CSV</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.v}
            onClick={() => setStatus(s.v)}
            data-testid={`admin-msg-filter-${s.v}`}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${status === s.v ? "" : "hover:bg-[color:var(--wofy-surface)]"}`}
            style={status === s.v ? { background: "var(--wofy-primary)", color: "#FDFBF7" } : {}}
          >
            {s.label}
            <span className="ml-2 text-xs font-mono opacity-60">{counts[s.v] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Inbox empty.</p>
        ) : items.map((m) => (
          <div
            key={m.id}
            data-testid={`admin-msg-${m.id}`}
            onClick={() => open(m)}
            className="wofy-card cursor-pointer hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider text-white"
                    style={{ background: STATUS_COLORS[m.status] || "#666" }}
                  >{m.status}</span>
                  <span className="font-medium">{m.subject}</span>
                </div>
                <p className="mt-1 text-sm line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{m.body}</p>
                <div className="mt-2 text-[11px] font-mono flex flex-wrap items-center gap-3" style={{ color: "var(--wofy-text-soft)" }}>
                  <span>{new Date(m.created_at).toLocaleString()}</span>
                  <span>{m.name}</span>
                  {m.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>}
                  {m.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <MessageDrawer
          m={selected}
          history={history}
          onClose={() => { setSelected(null); setHistory([]); }}
          onStatus={(s, c) => setStatusOn(selected, s, c)}
        />
      )}
    </div>
  );
}

function MessageDrawer({ m, history, onClose, onStatus }) {
  const [comment, setComment] = useState("");
  return (
    <div className="fixed inset-0 z-40 bg-[#14213D]/25 backdrop-blur-sm" onClick={onClose}>
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-[640px] overflow-y-auto p-8"
        style={{ background: "var(--wofy-paper)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-msg-drawer"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">MESSAGE</div>
            <h3 className="font-display text-2xl mt-1">{m.subject}</h3>
            <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              From <b>{m.name}</b> · {m.email || m.phone} · {new Date(m.created_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} className="wofy-btn-ghost text-xs">Close</button>
        </div>

        <div className="mt-6 wofy-card whitespace-pre-line text-sm">
          {m.body}
        </div>

        <div className="mt-6 wofy-card">
          <div className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Add a comment (optional)</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            data-testid="admin-msg-comment"
            className="w-full mt-3 rounded-lg border px-3 py-2 text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {["replied", "ignored", "archived", "deleted"].map((s) => (
              <button
                key={s}
                onClick={() => { onStatus(s, comment); setComment(""); }}
                data-testid={`admin-msg-action-${s}`}
                className="wofy-btn-ghost text-sm capitalize"
                style={s === "deleted" ? { color: "#B23A3A" } : {}}
              >
                Mark as {s}
              </button>
            ))}
          </div>
          {m.email && (
            <a
              href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`}
              className="wofy-btn-primary text-sm mt-3 inline-flex"
            >
              Reply by email
            </a>
          )}
        </div>

        <div className="mt-6">
          <div className="text-xs font-mono uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
            <History className="w-3 h-3" /> Audit history
          </div>
          <ol className="mt-3 space-y-3">
            {history.length === 0 && <li className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>No actions taken yet.</li>}
            {history.map((ev) => (
              <li key={ev.id} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--wofy-primary)" }}>
                <div className="text-xs font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>
                  {new Date(ev.created_at).toLocaleString()} · {ev.action}
                </div>
                {ev.meta?.from && <div className="text-xs">{ev.meta.from} → {ev.meta.to}</div>}
                {ev.comment && <div className="text-sm mt-1">"{ev.comment}"</div>}
                <div className="text-[11px] mt-1" style={{ color: "var(--wofy-text-soft)" }}>— {ev.admin_name}</div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
